import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatTimepickerModule } from '@angular/material/timepicker';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MindMatTimepickerInput } from './mind-mat-timepicker-input';
import { DateTimeUtils } from '../../utils/date-time-utils';
import { ValidatorErrorPipe } from '../../pipes/validator-error';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs';
import { isNil } from '@pct/ts-typeguard';
import { ServerTimeService } from '../../../core/server-time.service';

@Component({
  selector: 'app-mind-datetime',
  providers: [provideNativeDateAdapter()],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatTimepickerModule,
    MindMatTimepickerInput,
    ValidatorErrorPipe,
  ],
  templateUrl: './mind-datetime.component.html',
  styleUrl: './mind-datetime.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MindDatetimeComponent implements OnInit {
  readonly control = input.required<FormControl<Date | null>>();

  readonly label = input<string>('Date & Time');
  readonly required = input<boolean>(false);
  displayControl = new FormControl('');
  private userTyping = false;
  private readonly zone = inject(ServerTimeService).serverTimeZone;
  readonly wallValue = signal<Date | null>(null);

  get placeholderFormat(): string {
    const dateFmt = DateTimeUtils.getLocaleDateFormat();
    const timeFmt = 'HH:mm';
    return `${dateFmt} ${timeFmt}`;
  }

  constructor() {
    const control$ = toObservable(this.control);

    control$
      .pipe(
        switchMap(ctrl => ctrl.statusChanges),
        takeUntilDestroyed(),
      )
      .subscribe(() => {
        const ctrl = this.control();
        if (ctrl.disabled) {
          this.displayControl.disable({ emitEvent: false });
        } else {
          this.displayControl.enable({ emitEvent: false });
        }
      });

    control$
      .pipe(
        switchMap(ctrl => ctrl.valueChanges),
        takeUntilDestroyed(),
      )
      .subscribe((val: Date | null) => {
        if (!this.userTyping) {
          this.syncFromControl(val);
        }
      });
  }

  ngOnInit(): void {
    // initial disabled sync
    if (this.control().disabled) {
      this.displayControl.disable({ emitEvent: false });
    }

    this.syncFromControl(this.control().value);

    if (this.control().invalid && this.control().touched) {
      this.displayControl.setErrors(this.control().errors);
    } else {
      this.displayControl.setErrors(null);
    }
  }

  // Kézi gépelés lekezelése
  handleDateTimeInput(event: Event): void {
    this.userTyping = true;
    const inputVal = (event.target as HTMLInputElement).value;

    if (isNil(inputVal) || inputVal.trim() === '') {
      this.control().setValue(null);
      this.control().markAsTouched();
      return;
    }

    const parsed = DateTimeUtils.parseDateTime(inputVal);

    if (parsed !== null && parsed.isValid() && DateTimeUtils.formatDateTime(parsed.toDate()) === inputVal.trim()) {
      this.control().setValue(this.fromWall(parsed.toDate())); // vissza UTC-re
      this.control().setErrors(null);
      this.displayControl.setErrors(null);
    } else {
      this.control().setValue(null);
      this.control().setErrors({ invalidDate: true });
      this.displayControl.setErrors({ invalidDate: true });
    }

    this.control().markAsTouched();
    this.displayControl.markAsTouched();
  }

  onDatePartChange(newDate: Date | null): void {
    this.userTyping = false; //picker változtat, nem a user gépel
    if (newDate === null) {
      this.control().setValue(null);
      return;
    }
    const current = this.wallValue() ?? this.toWall(new Date())!; // "most" is zóna szerint
    const updated = new Date(newDate);
    updated.setHours(current.getHours(), current.getMinutes(), 0, 0);
    this.control().setValue(this.fromWall(updated));
    this.syncFromControl(this.control().value);
    this.control().markAsTouched();
  }

  onTimePartChange(newTime: Date | null): void {
    this.userTyping = false; //picker változtat, nem a user gépel
    if (newTime === null) return;
    const current = this.wallValue() ?? this.toWall(new Date())!;
    const updated = new Date(current);
    updated.setHours(newTime.getHours(), newTime.getMinutes(), 0, 0);
    this.control().setValue(this.fromWall(updated));
    this.syncFromControl(this.control().value);
    this.control().markAsTouched();
  }

  onBlur(): void {
    this.userTyping = false;
  }

  private toWall(d: Date | null): Date | null {
    return DateTimeUtils.toZoneWallClock(d, this.zone());
  }
  private fromWall(d: Date | null): Date | null {
    return DateTimeUtils.fromZoneWallClock(d, this.zone());
  }

  /** Display + picker érték szinkronizálás. */
  private syncFromControl(val: Date | null): void {
    const wall = this.toWall(val);
    this.wallValue.set(wall);
    this.displayControl.setValue(DateTimeUtils.formatDateTime(wall), { emitEvent: false });
  }
}
